import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';

/**
 * Range-Based FBX Loader
 * Wraps Three.js FBXLoader to force range-based requests for production compatibility
 */

export class RangeBasedFBXLoader {
    constructor(manager) {
        this.manager = manager;
        this.fbxLoader = new FBXLoader(manager);
    }

    async load(url, onLoad, onProgress, onError) {
        try {
            console.log('RangeBasedFBXLoader: Starting range-based load for:', url);
            
            // For streaming proxy endpoints, use custom range-based loading
            if (url.includes('/api/stream_model/')) {
                await this.loadWithRangeRequests(url, onLoad, onProgress, onError);
            } else {
                // Use standard FBXLoader for other URLs
                this.fbxLoader.load(url, onLoad, onProgress, onError);
            }
        } catch (error) {
            console.error('RangeBasedFBXLoader: Load failed:', error);
            if (onError) onError(error);
        }
    }

    async loadWithRangeRequests(url, onLoad, onProgress, onError) {
        try {
            console.log('RangeBasedFBXLoader: Using range-based loading for:', url);
            
            // Consume the in-flight first-byte fetch the lab page started
            // during HTML head parse, if it matches this URL. Otherwise
            // issue the size probe ourselves.
            let sizeResponse;
            const preload =
                typeof window !== 'undefined' ? window.__modelPreload : null;
            if (preload && preload.url === url && preload.promise) {
                window.__modelPreload = null;
                try {
                    sizeResponse = await preload.promise;
                } catch (_) {
                    sizeResponse = null;
                }
            }
            if (!sizeResponse) {
                sizeResponse = await fetch(url, {
                    method: 'GET',
                    headers: { 'Range': 'bytes=0-0' },
                });
            }

            if (typeof window !== 'undefined' && window.__characterClickAt) {
                const elapsed =
                    performance.timeOrigin + performance.now() -
                    window.__characterClickAt;
                console.log(
                    '[Perf] Click → first FBX byte:',
                    Math.round(elapsed),
                    'ms',
                );
                window.__characterClickAt = 0;
            }

            if (!sizeResponse.ok) {
                throw new Error(`Failed to get file size: ${sizeResponse.status}`);
            }

            const contentRange = sizeResponse.headers.get('Content-Range');
            if (!contentRange) {
                throw new Error('Server does not support range requests');
            }

            // Parse the total file size from Content-Range header
            // Format: "bytes 0-0/68521328"
            const totalSize = parseInt(contentRange.split('/')[1]);
            console.log(`RangeBasedFBXLoader: File size: ${totalSize} bytes`);

            const resolvedUrl = sizeResponse.url || url;
            if (resolvedUrl !== url) {
                console.log('RangeBasedFBXLoader: Resolved URL after redirect:', resolvedUrl);
            }

            // Download the file in chunks using range requests
            const chunkSize = 1024 * 1024; // 1MB chunks
            const chunks = [];
            let downloaded = 0;

            for (let start = 0; start < totalSize; start += chunkSize) {
                const end = Math.min(start + chunkSize - 1, totalSize - 1);
                
                console.log(`RangeBasedFBXLoader: Downloading chunk ${start}-${end}`);
                
                const chunkResponse = await fetch(resolvedUrl, {
                    method: 'GET',
                    headers: {
                        'Range': `bytes=${start}-${end}`
                    }
                });

                if (!chunkResponse.ok) {
                    throw new Error(`Chunk download failed: ${chunkResponse.status}`);
                }

                const chunkData = await chunkResponse.arrayBuffer();
                chunks.push(new Uint8Array(chunkData));
                downloaded += chunkData.byteLength;

                // Report progress
                if (onProgress) {
                    onProgress({
                        lengthComputable: true,
                        loaded: downloaded,
                        total: totalSize
                    });
                }
            }

            // Combine all chunks into a single ArrayBuffer
            console.log('RangeBasedFBXLoader: Combining chunks...');
            const completeFile = new Uint8Array(totalSize);
            let offset = 0;
            
            for (const chunk of chunks) {
                completeFile.set(chunk, offset);
                offset += chunk.length;
            }

            // Create a Blob URL for the complete file
            const blob = new Blob([completeFile], { type: 'application/octet-stream' });
            const blobUrl = URL.createObjectURL(blob);

            console.log('RangeBasedFBXLoader: Loading FBX from blob URL...');
            
            // Use the standard FBXLoader with the blob URL
            this.fbxLoader.load(
                blobUrl,
                (model) => {
                    // Clean up blob URL
                    URL.revokeObjectURL(blobUrl);
                    console.log('RangeBasedFBXLoader: FBX loaded successfully');
                    if (onLoad) onLoad(model);
                },
                (progress) => {
                    // FBX parsing progress (already downloaded)
                    console.log('RangeBasedFBXLoader: FBX parsing progress:', progress);
                },
                (error) => {
                    // Clean up blob URL on error
                    URL.revokeObjectURL(blobUrl);
                    console.error('RangeBasedFBXLoader: FBX parsing failed:', error);
                    if (onError) onError(error);
                }
            );

        } catch (error) {
            console.error('RangeBasedFBXLoader: Range-based loading failed:', error);
            if (onError) onError(error);
        }
    }
}